// src/schedule/DayRow.js
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { MdSettings } from 'react-icons/md';
import PortalDropdown from './PortalDropdown';
import Avatar from './Avatar';
import ColorPickerPopover from './components/ColorPickerPopover';
import EventBox from './components/EventBox';
import { parseAssignmentKey, getWeekdayName, resolveEventIndex, buildEventLocatorKey } from '../utils/dateHelpers';
import { useEventNotes } from '../hooks/useEventNotes';
import { useNeededPatches } from '../hooks/useNeededPatches';
import { useMonthLayout } from '../hooks/useMonthLayout'; // eventy + placeholdery
import { normalizeStaffEntries, hasVisibleUnavailable } from '../utils/staffCopyConflicts';

const DayRow = ({
    date,
    todayStr,
    events,
    allEventsForDay,
    employees,
    updateEventStaff,
    updateEventColor,
    updateNeededForEvent,
    columnAssignments,
    unavailable,
    toggleUnavailable: toggleUnavailableProp,
    forcedWidth,
    isAdmin,
    copyStaffToAllDates,
    unavailableByDate,
    eventsByDateFull,
    getAssignedNamesForDate,
    getUnavailableNamesForDate,
    activeCalendarId,
    dragScale = 1,
    // mobile: czy karta tego dnia jest aktualnie powiększona
    isExpanded = false,
    // ukryty oryginał podczas zoomu — nie mierz layoutu (zerowa geometria psuje skalę)
    layoutFrozen = false,
    onCloseExpanded,
}) => {
    const [showUnavailableMenu, setShowUnavailableMenu] = useState(false);
    const [openNeededMenu, setOpenNeededMenu] = useState(null); // keyId
    const [openMenu, setOpenMenu] = useState(null);             // keyId
    const [openNoteKey, setOpenNoteKey] = useState(null);       // keyId
    const [noteDraft, setNoteDraft] = useState('');
    const [pendingColor, setPendingColor] = useState('#888888');
    const [colorAnchor, setColorAnchor] = useState(null);
    const [colorOpen, setColorOpen] = useState(false);
    const [colorTarget, setColorTarget] = useState(null);

    // Needed portal root (pinned przy ⚙️)
    const [neededPortalRoot, setNeededPortalRoot] = useState(null);

    // Unavailable (LS fallback)
    const [localUnavailable, setLocalUnavailable] = useState(new Set());

    // Refs
    const unavailBtnRef = useRef(null);
    const actionContainersRef = useRef({}); // map: keyId -> div.relative (kontener menu)
    const menuOverlayRef = useRef(null);
    const neededPanelRef = useRef(null);

    const empList = Array.isArray(employees) ? employees : [];
    
    // Hook do zarządzania layoutem miesiąca
    const monthKey = date.slice(0, 7); // 'YYYY-MM'
    const { scaleBoxRef, scaleInnerRef, targetCols } = useMonthLayout(
        monthKey,
        date,
        events,
        columnAssignments,
        { freeze: isExpanded || layoutFrozen }
    );

    const useLSFallback = typeof toggleUnavailableProp !== 'function';
    const LS_KEY = (d) => `unavail:${d}`;
    const readLS = useCallback((d) => {
        try {
            const arr = JSON.parse(localStorage.getItem(LS_KEY(d)) || '[]');
            return new Set(Array.isArray(arr) ? arr.filter((x) => typeof x === 'string') : []);
        } catch { return new Set(); }
    }, []);
    const writeLS = (d, set) => { try { localStorage.setItem(LS_KEY(d), JSON.stringify(Array.from(set))); } catch { } };

    useEffect(() => {
        const base = getUnavailableNamesForDate ? getUnavailableNamesForDate(date) : new Set();
        const persist = useLSFallback ? new Set([...base, ...readLS(date)]) : new Set(base);
        setLocalUnavailable(persist);
    }, [date, getUnavailableNamesForDate, useLSFallback, readLS]);

    useEffect(() => {
        if (!showUnavailableMenu) return;
        const onResize = () => setShowUnavailableMenu(false);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, [showUnavailableMenu]);

    // Zamykaj menu ikon po kliknięciu poza (wraz z Needed)
    useEffect(() => {
        const onDocDown = (e) => {
            if (!openMenu) return;
            const container = actionContainersRef.current[openMenu];
            const inMenu = menuOverlayRef.current && menuOverlayRef.current.contains(e.target);
            const inNeeded = neededPanelRef.current && neededPanelRef.current.contains(e.target);
            if (!inMenu && !inNeeded && !(container && container.contains(e.target))) {
                setOpenMenu(null);
                if (openNeededMenu) {
                    setOpenNeededMenu(null);
                    setNeededPortalRoot(null);
                }
            }
        };
        document.addEventListener('mousedown', onDocDown, true);
        return () => document.removeEventListener('mousedown', onDocDown, true);
    }, [openMenu, openNeededMenu]);

    // Hooki
    const { readNote, writeNote } = useEventNotes(date);

    const callUpdateColor = (eventName, calendarId, color) => {
        if (typeof updateEventColor !== 'function') return;
        const calId = calendarId ?? activeCalendarId;
        try {
            if (updateEventColor.length <= 2) updateEventColor(eventName, color);
            else updateEventColor(date, eventName, color, calId, true);
        } catch { }
    };

    const assignedNamesToday = getAssignedNamesForDate ? getAssignedNamesForDate(date) : new Set();

    const propsUnavailable = getUnavailableNamesForDate ? getUnavailableNamesForDate(date) : new Set();
    const persistentUnavailable = useLSFallback ? new Set([...propsUnavailable, ...readLS(date)]) : propsUnavailable;
    const filterUnavailable = showUnavailableMenu ? localUnavailable : persistentUnavailable;
    const hasAnyUnavailable = hasVisibleUnavailable(filterUnavailable, assignedNamesToday);

    const menuCandidates = empList.filter((emp) => {
        const name = typeof emp === 'string' ? emp : emp.name;
        return !assignedNamesToday.has(name);
    });

    const allForDayArr = Array.isArray(allEventsForDay) ? allEventsForDay : [];

    const callToggleUnavailable = (name) => {
        const fn = toggleUnavailableProp;
        if (typeof fn !== 'function') return;
        try { return fn.length >= 2 ? fn(date, name) : fn(name); } catch { }
    };

    const handleToggleUnavailable = (name) => {
        setLocalUnavailable((prev) => {
            const next = new Set(prev);
            next.has(name) ? next.delete(name) : next.add(name);
            if (useLSFallback) writeLS(date, next);
            return next;
        });
        if (!useLSFallback) callToggleUnavailable(name);
    };

    // --- helper: ustaw needed korzystając z obu możliwych sygnatur
    const callUpdateNeeded = (d, eventName, needed, calendarId, gId) => {
        if (typeof updateNeededForEvent !== 'function') return false;
        const calId = calendarId ?? activeCalendarId;
        const identifier = gId
            ? { gId, event: eventName, calendarId: calId }
            : { event: eventName, calendarId: calId };

        try {
            updateNeededForEvent(d, identifier, needed, calId, true);
            return true;
        } catch { /* ignore */ }
        return false;
    };

    // Hook do zarządzania needed patches
    useNeededPatches(date, callUpdateNeeded);

    const doCopyStaff = async (startDate, eventNameOrObj, normalizedStaff, clampedNeeded, calId) => {
        if (typeof copyStaffToAllDates !== 'function') return;
        await copyStaffToAllDates(
            startDate,
            eventNameOrObj,
            normalizeStaffEntries(normalizedStaff),
            clampedNeeded,
            calId
        );
    };


    return (
        <>
            <div
                className={`day-card ${isExpanded ? 'day-card--zoomed' : ''} ${date === todayStr ? 'bg-yellow-800' : ''}`}
                style={{
                    width: forcedWidth || '100%',
                    backgroundColor: date === todayStr ? '#92400e' : undefined,
                    overflowX: 'hidden',
                    // Nie ustawiaj żadnych stylów wysokości gdy nie jest powiększony - pozwól CSS działać normalnie
                    ...(isExpanded ? {} : {})
                }}
            >
                <div className="flex justify-between items-center mb-3">
                    <div className="font-bold text-lg">📆 {new Date(date).getDate()} ({getWeekdayName(date)})</div>
                    <div className="flex items-center gap-2">
                        <button
                            ref={unavailBtnRef}
                            onClick={(e) => {
                                e.stopPropagation();
                                setShowUnavailableMenu((prev) => !prev);
                            }}
                            className={`cursor-pointer relative ${hasAnyUnavailable ? 'text-red-500 dark:text-red-400' : ''}`}
                            aria-expanded={showUnavailableMenu}
                            type="button"
                            title={hasAnyUnavailable ? 'Są osoby niedostępne' : 'Zarządzaj niedostępnościami'}
                        >
                            📋
                            {hasAnyUnavailable && <span className="absolute -inset-1 border-2 border-red-500 rounded pointer-events-none"></span>}
                        </button>

                        <PortalDropdown
                            anchorRef={unavailBtnRef}
                            open={showUnavailableMenu}
                            onClose={() => setShowUnavailableMenu(false)}
                            align="right"
                            offset={8}
                            minWidth={320}
                            maxHeight={360}
                        >
                            <div className="p-2" onClick={(e) => e.stopPropagation()}>
                                {menuCandidates.map((emp, idx) => {
                                    const empName = typeof emp === 'string' ? emp : emp.name;
                                    const isUnavail = localUnavailable.has(empName);
                                    return (
                                        <div
                                            key={idx}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleToggleUnavailable(empName);
                                            }}
                                            className={`px-2 py-2 cursor-pointer rounded flex items-center gap-2
                        ${isUnavail ? 'bg-red-600 text-white dark:bg-red-700' : 'hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                                        >
                                            <Avatar employee={emp} size={20} />
                                            <span>{empName}</span>
                                        </div>
                                    );
                                })}
                                {!menuCandidates.length && (
                                    <div className="text-sm text-gray-600 dark:text-gray-400 px-2 py-1">Brak kandydatów</div>
                                )}
                            </div>
                        </PortalDropdown>
                    </div>
                </div>

                {/* === NORMALIZACJA + SKALA/ROZCIĄGANIE PER MIESIĄC (brak poziomego scrolla) === */}
                <div 
                    ref={scaleBoxRef} 
                    className="day-events-scale-box" 
                    style={{ 
                        position: 'relative', 
                        width: '100%', 
                        overflow: 'hidden',
                        // Ustaw wysokość tylko gdy jest powiększony, w przeciwnym razie nie ustawiaj nic
                        ...(isExpanded ? { height: '100%', minHeight: '100%' } : {})
                    }}
                >
                    <div 
                        ref={scaleInnerRef} 
                        className="day-events-scale-inner" 
                        style={{ 
                            transformOrigin: 'left top',
                            // Ustaw wysokość tylko gdy jest powiększony, w przeciwnym razie nie ustawiaj nic
                            ...(isExpanded ? { height: '100%', minHeight: '100%' } : {})
                        }}
                    >
                        {(() => {
                            const entries = Object.entries(columnAssignments)
                                .filter(([key]) => key.startsWith(date))
                                .sort((a, b) => a[1] - b[1]);

                            // Oblicz liczbę rzeczywistych wydarzeń (bez placeholderów)
                            const realEventsCount = entries.filter(([key]) => {
                                const { isPlaceholder } = parseAssignmentKey(key);
                                return !isPlaceholder;
                            }).length;
                            
                            // Style dla grid w trybie expanded - różne układy w zależności od liczby wydarzeń
                            let expandedGridStyle = {};
                            if (isExpanded) {
                                if (realEventsCount === 0) {
                                    // Brak wydarzeń - nie powinno się zdarzyć, ale na wszelki wypadek
                                    expandedGridStyle = {
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        height: '100%'
                                    };
                                } else if (realEventsCount === 1) {
                                    // 1 wydarzenie - centralnie na całej powierzchni
                                    expandedGridStyle = {
                                        display: 'grid',
                                        gridTemplateColumns: '1fr',
                                        gridTemplateRows: '1fr',
                                        gap: '0.75rem',
                                        overflowY: 'auto',
                                        overflowX: 'hidden',
                                        height: '100%',
                                        placeItems: 'center'
                                    };
                                } else if (realEventsCount === 2) {
                                    // 2 wydarzenia - jedno nad drugim na całą szerokość
                                    expandedGridStyle = {
                                        display: 'grid',
                                        gridTemplateColumns: '1fr',
                                        gridTemplateRows: '1fr 1fr',
                                        gap: '0.75rem',
                                        overflowY: 'auto',
                                        overflowX: 'hidden',
                                        height: '100%'
                                    };
                                } else {
                                    // 3+ wydarzenia - dwie kolumny
                                    const evenRowsCount = Math.ceil(realEventsCount / 2);
                                    expandedGridStyle = {
                                        display: 'grid',
                                        gridTemplateColumns: '1fr 1fr',
                                        gridTemplateRows: `repeat(${evenRowsCount}, 1fr)`,
                                        gap: '0.75rem',
                                        overflowY: 'auto',
                                        overflowX: 'hidden',
                                        height: '100%'
                                    };
                                }
                            }

                            let realCount = 0;
                            let phCount = 0;

                                const jsx = entries.map(([key]) => {
                                    const { eventName, calendarId, gId, isPlaceholder } = parseAssignmentKey(key);

                                    if (isPlaceholder) {
                                        phCount++;
                                        // W trybie expanded nie renderuj placeholderów
                                        if (isExpanded) return null;
                                        return (
                                            <div
                                                key={key}
                                                className="event-box"
                                                data-ph="1"
                                                style={{ flex: '0 0 220px', minWidth: '220px', width: '220px' }}
                                            />
                                        );
                                    }

                                    const evtIndex = resolveEventIndex(events, {
                                        gId,
                                        eventName,
                                        calendarId: calendarId ?? activeCalendarId,
                                    });
                                    if (evtIndex === -1) return null;

                                    realCount++;
                                    const evt = events[evtIndex];
                                    if (!evt) return null;

                                    const assignedNames = (Array.isArray(allEventsForDay) ? allEventsForDay : []).flatMap((ev) =>
                                        (ev.staff || []).map((sx) => (typeof sx === 'object' ? sx?.name : sx))
                                    );

                                    const available = empList.filter((emp) => {
                                        const name = typeof emp === 'string' ? emp : emp.name;
                                        const isAssigned = assignedNames.includes(name);
                                        const isUnavail = filterUnavailable.has(name);
                                        return !isAssigned && !isUnavail;
                                    });

                                    const neededNumRaw = Number(evt?.needed);
                                    const needed = Number.isFinite(neededNumRaw) && neededNumRaw >= 1 ? neededNumRaw : Math.max(1, (evt?.staff?.length || 0));
                                    const freeSlots = Math.max(0, needed - (evt?.staff?.length || 0));

                                    const droppableId = buildEventLocatorKey(date, {
                                        gId: evt.gId,
                                        event: evt.event,
                                        calendarId: evt.calendarId ?? 'noCal',
                                    });
                                    const noteText = readNote(evt);
                                    const hasNote = noteText.trim().length > 0;
                                    const keyId = buildEventLocatorKey(date, {
                                        gId: evt.gId,
                                        event: evt.event,
                                        calendarId: evt.calendarId ?? activeCalendarId ?? 'noCal',
                                    });

                                    return (
                                        <EventBox
                                            key={key}
                                            evt={evt}
                                            date={date}
                                            evtIndex={evtIndex}
                                            keyId={keyId}
                                            droppableId={droppableId}
                                            employees={empList}
                                            allEventsForDay={allEventsForDay}
                                            isAdmin={isAdmin}
                                            updateEventStaff={updateEventStaff}
                                            updateEventColor={callUpdateColor}
                                            updateNeededForEvent={updateNeededForEvent}
                                            activeCalendarId={activeCalendarId}
                                            eventsByDateFull={eventsByDateFull}
                                            unavailableByDate={unavailableByDate}
                                            filterUnavailable={filterUnavailable}
                                            readNote={readNote}
                                            writeNote={writeNote}
                                            noteText={noteText}
                                            hasNote={hasNote}
                                            openNoteKey={openNoteKey}
                                            noteDraft={noteDraft}
                                            onNoteChange={(val) => setNoteDraft(val)}
                                            onNoteSave={() => { writeNote(evt, noteDraft); setOpenNoteKey(null); }}
                                            onNoteCancel={() => setOpenNoteKey(null)}
                                            onNoteClear={() => { writeNote(evt, ''); setNoteDraft(''); setOpenNoteKey(null); }}
                                            openMenu={openMenu}
                                            menuOverlayRef={menuOverlayRef}
                                            onMenuToggle={(id) => setOpenMenu(openMenu === id ? null : id)}
                                            onColorClick={(e) => {
                                                setColorAnchor(e.currentTarget);
                                                setPendingColor(evt.color || '#888888');
                                                setColorTarget({ eventName: evt.event, calendarId: evt.calendarId ?? activeCalendarId });
                                                setColorOpen(true);
                                            }}
                                            onCopyClick={() => {
                                                const calId = evt.calendarId ?? activeCalendarId;
                                                const staffLen = (evt.staff || []).length;
                                                const rawNeeded = Number(evt.needed);
                                                const need = Number.isFinite(rawNeeded) && rawNeeded >= 1 ? rawNeeded : Math.max(1, staffLen);
                                                const clampedNeeded = Math.max(need, staffLen, 1);
                                                const eventIdentifier = evt?.gId ? evt : evt.event;
                                                doCopyStaff(date, eventIdentifier, evt.staff || [], clampedNeeded, calId);
                                            }}
                                            onNeededClick={() => {
                                                const root = actionContainersRef.current[keyId];
                                                setNeededPortalRoot(root || null);
                                                setOpenNeededMenu(keyId);
                                            }}
                                            onNoteClick={() => { setOpenNoteKey(keyId); setNoteDraft(noteText); }}
                                            openNeededMenu={openNeededMenu}
                                            neededPortalRoot={neededPortalRoot}
                                            neededPanelRef={neededPanelRef}
                                            actionContainersRef={actionContainersRef}
                                            onNeededClose={() => {
                                                setOpenNeededMenu(null);
                                                setNeededPortalRoot(null);
                                            }}
                                            onRegisterPanelRef={(el) => { neededPanelRef.current = el; }}
                                            isExpanded={isExpanded}
                                        />
                                    );
                                });

                                // auto-placeholdery, by każdy dzień miał tyle samo kolumn (wg max w miesiącu)
                                // W trybie powiększenia pomijamy je, aby nie wyświetlać pustych kart
                                if (!isExpanded && targetCols) {
                                    const currentCols = realCount + phCount;
                                    const needAuto = Math.max(targetCols - currentCols, 0);
                                    for (let i = 0; i < needAuto; i++) {
                                        jsx.push(
                                            <div
                                                key={`auto-ph-${i}`}
                                                className="event-box"
                                                data-ph="1"
                                                data-auto="1"
                                                style={{ flex: '0 0 220px', minWidth: '220px', width: '220px' }}
                                            />
                                        );
                                    }
                                }

                            return (
                                <div 
                                    key="events-wrapper"
                                    className="flex gap-4 day-events-wrapper" 
                                    style={isExpanded ? expandedGridStyle : undefined}
                                >
                                    {jsx}
                                </div>
                            );
                        })()}
                    </div>
                </div>
            </div>

            <ColorPickerPopover
                anchorEl={colorAnchor}
                open={!!colorOpen && !!colorAnchor}
                value={pendingColor || '#888888'}
                onChange={(c) => setPendingColor(c)}
                onCancel={() => {
                    setColorOpen(false);
                    setColorAnchor(null);
                    setColorTarget(null);
                }}
                onSave={() => {
                    if (colorTarget) {
                        const { eventName, calendarId } = colorTarget;
                        callUpdateColor(eventName, calendarId, pendingColor || '#888888');
                    }
                    setColorOpen(false);
                    setColorAnchor(null);
                    setColorTarget(null);
                }}
                width={320}
                offset={10}
            />
        </>
    );
};

export default DayRow;
